import { Utterance } from "../types/utterance";
import type { Transcriber, TranscriptionState } from "./Transcriber";

import { MicTranscriber } from "./MicTranscriber";
import { RoomHub } from "../Multidevice/RoomHub";

type Role = "host" | "guest";

export class MultiDeviceTranscriber implements Transcriber {
  private state: TranscriptionState = "idle";
  private utterSubs = new Set<(u: Utterance) => void>();
  private stateSubs = new Set<(s: TranscriptionState) => void>();
  private errorSubs = new Set<(e: Error) => void>();

  private hub = new RoomHub();
  private mic?: MicTranscriber;
  private offGuest?: () => void;
  private stopLocal?: () => Promise<void>;
  private offPresence?: () => void;
  private sentHostIds = new Set<string>();

  constructor(
    private sessionId: string,
    private opts: {
      role: Role;
      captureLocalMic?: boolean; // default true for host, false for guest
      speaker1Lang?: string;
      speaker2Lang?: string;
      onPresence?: (p: {
        sessionId: string;
        state: string;
        ts?: number;
      }) => void;
    } = { role: "host" }
  ) {}

  getState() {
    return this.state;
  }
  private setState(s: TranscriptionState) {
    this.state = s;
    this.stateSubs.forEach((fn) => fn(s));
  }
  onUtterance(cb: (u: Utterance) => void) {
    this.utterSubs.add(cb);
    return () => this.utterSubs.delete(cb);
  }
  onState(cb: (s: TranscriptionState) => void) {
    this.stateSubs.add(cb);
    return () => this.stateSubs.delete(cb);
  }
  onError(cb: (e: Error) => void) {
    this.errorSubs.add(cb);
    return () => this.errorSubs.delete(cb);
  }

  async start(opts: { languages: string[] }) {
    this.setState("connecting");
    this.offPresence = this.hub.onGuestPresence((p) => {
      this.opts.onPresence?.(p);
    });

    try {
      // 1) connect hub + join room
      await this.hub.connect();
      await this.hub.join(this.sessionId);

      // 2) subscribe to remote guest utterances → emit as Speaker 2
      this.offGuest = this.hub.onGuestUtter((g) => {
        if (g.sessionId !== this.sessionId) return;
        const u: Utterance = {
          speaker: 2,
          text: g.text,
          lang: g.lang || this.opts.speaker2Lang,
          confidence: g.confidence,
          start: 0,
          end: 0,
        };
        this.utterSubs.forEach((fn) => fn(u));
      });

      // 3) optionally capture local mic (host usually yes; guest usually no)
      // 3) optionally capture local mic (host usually yes; guest usually no)
      const captureLocal =
        this.opts.captureLocalMic ?? this.opts.role === "host";

      if (captureLocal) {
        this.mic = new MicTranscriber();

        // ⬇️ REPLACE your existing onUtterance with this block
        this.mic.onUtterance((u) => {
          // local to UI as Speaker 1
          const hostUtter = {
            ...u,
            speaker: 1,
            lang: u.lang || this.opts.speaker1Lang,
          };
          this.utterSubs.forEach((fn) => fn(hostUtter));

          // also broadcast to guests via RoomHub
          try {
            const tsMs = Math.round(u.start ? u.start * 1000 : Date.now());
            const id =
              (u as any).__eventId || `${tsMs}-${(u.text || "").slice(0, 32)}`;

            if (!this.sentHostIds.has(id)) {
              this.sentHostIds.add(id);
              this.hub
                .sendHostUtter({
                  sessionId: this.sessionId,
                  text: u.text,
                  lang: hostUtter.lang,
                  confidence: u.confidence,
                  ts: tsMs,
                  id,
                  from: "host",
                })
                .catch((e) => console.warn("sendHostUtter failed:", e));
            }
          } catch (e) {
            console.warn("host broadcast failed:", e);
          }
        });
       
        this.mic.onError((e) => this.errorSubs.forEach((fn) => fn(e)));
        this.mic.onState((s) => this.stateSubs.forEach((fn) => fn(s)));
        await this.mic.start({ languages: opts.languages });
      } else {
        this.setState("listening");
      }
    } catch (e: any) {
      this.setState("error");
      this.errorSubs.forEach((fn) => fn(e));
      throw e;
    }
  }

  async stop() {
    try {
      this.offGuest?.();
      this.offGuest = undefined;
      this.offPresence?.();
      this.offPresence = undefined;
      this.sentHostIds.clear();
      await this.mic?.stop();
      await this.hub.disconnect();
    } finally {
      this.setState("stopped");
    }
  }
}
